import { skipToken } from '@apollo/client/react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  getArtworkShareLink,
  sharePlatformMap,
} from 'src/components/SocialShare/SocialShare'
import { useCopy } from 'src/hooks/useCopy'
import { useGetArtworkWithTaskDetail } from 'src/services/apollo/artwork'
import { useMyInfo } from 'src/services/apollo/auth'
import { useGetGenerationModelByVersionIdForArtwork } from 'src/services/apollo/generation-model'
import {
  executeClaimShareCredit,
  useGetMyShareCreditInfo,
} from 'src/services/apollo/quota'
import { isAnimatedArtwork } from 'src/utils/artwork'
import { translateErrorCode } from 'src/utils/errorCodeTranslator'
import { getErrorCode } from 'src/utils/errorUtil'
import { getMediaUrl, getPublicUrl } from 'src/utils/media'
import { isMMDIT26AModel } from 'src/utils/model'
import { formatParamURL } from 'src/utils/qs'
import { track } from 'src/utils/tracker'
import { isMobileDevice } from 'src/utils/ua'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import {
  downloadFile,
  getImageDownloadFileExt,
  getImageDownloadFilename,
} from '../utils'
import type { ArtworkForShare } from './ArtworkShareContent'
import { normalizeTackNameForShare } from './utils'

function getBrandSuffix(isTsubaki2: boolean) {
  return isTsubaki2 ? '#PixAI #PixAITsubaki2' : '#PixAI'
}

function useIsTsubaki2(artworkId: string) {
  const { data: artworkData } = useGetArtworkWithTaskDetail(
    { id: artworkId },
    {
      fetchPolicy: 'cache-only',
    },
  )
  type GeneratorTaskParameters = {
    modelId?: string
  }
  const generatorParams = artworkData?.taskParameters?.parameters as
    | GeneratorTaskParameters
    | undefined
  const modelVersionId = generatorParams?.modelId
  const { data: modelVersion } = useGetGenerationModelByVersionIdForArtwork(
    modelVersionId ?? skipToken,
    { fetchPolicy: 'cache-only' },
  )
  return isMMDIT26AModel(modelVersion?.model?.type)
}

export function useShareAction(props: {
  artwork: ArtworkForShare
  scene?: string
}) {
  const { artwork, scene } = props
  const { t } = useTranslation(['artwork'])
  const [copy, copied] = useCopy()
  const artworkImageUrl = getPublicUrl(artwork.media)
  const isTsubaki2 = useIsTsubaki2(artwork.id)

  const shareText = useMemo(() => {
    const brand = getBrandSuffix(isTsubaki2)
    if (artwork.title) return `${artwork.title} ${brand}`
    const firstTack = artwork.tacks?.[0]
    if (firstTack) {
      const localizedName = firstTack.displayName ?? firstTack.defaultName
      const tag = normalizeTackNameForShare(localizedName) || localizedName
      return t('artwork:share.share-text.with-tag', { tag, brand })
    }
    return t('artwork:share.share-text.fallback', { brand })
  }, [artwork.title, artwork.tacks, isTsubaki2, t])

  const { data: userInfo } = useMyInfo()
  const { data: { countRemains, amountEach } = {} } = useGetMyShareCreditInfo(
    userInfo?.id ? {} : skipToken,
  )

  const [lastClaimedAmount, setLastClaimedAmount] = useState<number | null>(
    null,
  )

  // Mobile save-guide: when a shouldCopy platform is clicked on mobile,
  // we show a save-image overlay instead of downloading, then redirect after.
  const [pendingRedirect, setPendingRedirect] = useState<{
    href: string
    platform: string
    label: string
  } | null>(null)

  const [sharing, onSharing] = useLoadingCallback(
    async (platform?: string) => {
      if (!userInfo?.id) return
      if ((countRemains ?? 0) < 1) return
      if (amountEach == null) return

      try {
        await executeClaimShareCredit({})
        setLastClaimedAmount(amountEach)
        track('share_credits_dialog_show', {
          fromPath: platform,
          scene,
        })
      } catch (e: any) {
        toast.error(
          translateErrorCode(getErrorCode(e), { fallback: e.message }),
        )
      }
    },
    [userInfo?.id, countRemains, amountEach],
  )

  const onShareClick = useCallback(
    (platform: string) => {
      if (sharing) return

      track('share_platform_btn_click', {
        value: platform,
        scene,
      })
      return onSharing(platform)
    },
    [sharing, onSharing, scene],
  )

  const shareProps = useCallback(
    (platform: keyof typeof sharePlatformMap) => {
      const data = sharePlatformMap[platform]
      const shareLink = formatParamURL(getArtworkShareLink(artwork.id), {
        utm_medium: 'referral',
        utm_source: platform,
        utm_content: 'share',
      })
      const allTags =
        artwork.tacks
          ?.map(t => t.displayName ?? t.defaultName)
          .map(name => normalizeTackNameForShare(name))
          .filter(Boolean) ?? []
      // When there's no title, the first tack is already in shareText — skip it
      const artworkTags =
        !artwork.title && allTags.length > 0 ? allTags.slice(1) : allTags
      const href = data.shareUrl({
        link: shareLink,
        image: artworkImageUrl,
        text: shareText,
        tags: artworkTags,
      })

      return {
        href,
        target: '_blank' as const,
        rel: 'noopener noreferrer',
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
          onShareClick(platform)
          if ('shouldCopy' in data && data.shouldCopy) {
            e.preventDefault()
            e.stopPropagation()

            if (isMobileDevice()) {
              // Mobile: show save-image guide, then redirect
              setPendingRedirect({ href, platform, label: data.label })
            } else {
              // Desktop: download image then open platform
              const imageUrl =
                getMediaUrl(artwork.videoMediaId, 'orig') ||
                getPublicUrl(artwork.media)
              if (imageUrl) {
                const isAnimated = isAnimatedArtwork(artwork)
                const imageExt = getImageDownloadFileExt({
                  artwork,
                  media: artwork.media,
                })
                const imageFileName = `${getImageDownloadFilename({ artwork })}.${imageExt}`
                downloadFile(imageUrl, imageFileName, {
                  skipConversion: isAnimated,
                }).catch(() => {
                  // Silently ignore download errors
                })
              }
              window.open(href, '_blank', 'noopener,noreferrer')
            }
          }
        },
      }
    },
    [artwork, artworkImageUrl, onShareClick, shareText],
  )

  const supportsNativeShare =
    typeof navigator !== 'undefined' && !!navigator.share && isMobileDevice()

  const onNativeShare = useCallback(async () => {
    const shareLink = formatParamURL(getArtworkShareLink(artwork.id), {
      utm_medium: 'referral',
      utm_source: 'native_share',
      utm_content: 'share',
    })
    try {
      await navigator.share({
        text: shareText,
        url: shareLink,
      })
      track('share_platform_btn_click', {
        value: 'native_share',
        scene,
      })
      onSharing('native_share')
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('Native share failed:', err)
    }
  }, [artwork.id, shareText, scene, onSharing])

  const copyLink = useCallback(() => {
    track('copy_link_click', {
      scene,
      location: 'share',
    })
    copy(
      formatParamURL(getArtworkShareLink(artwork.id), {
        utm_source: 'copy_web',
      }),
    )
  }, [artwork.id, scene, copy])

  const confirmPendingRedirect = useCallback(() => {
    if (pendingRedirect) {
      window.open(pendingRedirect.href, '_blank', 'noopener,noreferrer')
      setPendingRedirect(null)
    }
  }, [pendingRedirect])

  const dismissPendingRedirect = useCallback(() => {
    setPendingRedirect(null)
  }, [])

  return {
    shareProps,
    onNativeShare,
    supportsNativeShare,
    sharing,
    copied,
    copyLink,
    countRemains,
    amountEach,
    lastClaimedAmount,
    clearClaimedAmount: useCallback(() => setLastClaimedAmount(null), []),
    isLoggedIn: !!userInfo?.id,
    pendingRedirect,
    confirmPendingRedirect,
    dismissPendingRedirect,
  }
}
